import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Shield,
} from "lucide-react";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  AuthField,
  AuthShell,
} from "@jambushrushti/site/components/auth/AuthShell";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<"login" | "signup">("login");
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    login,
    signup,
    isAuthenticated,
    isLoading: authLoading,
    user,
  } = useAdminAuth();

  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Only redirect after login completes with a valid redirect path
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate]);

  if (authLoading) {
    return (
      <div className="jbs-site au-loading" aria-busy="true">
        <Loader2 className="au-spin" aria-label="Loading" />
      </div>
    );
  }

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Password managers/browser autofill do not always emit React onChange.
    // Read the rendered inputs so the visible credentials are submitted.
    const submittedEmail = emailInputRef.current?.value.trim() || email.trim();
    const submittedPassword = passwordInputRef.current?.value || password;

    if (!submittedEmail || !submittedPassword) {
      toast({
        title: "Missing credentials",
        description: "Please enter your email and password",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(submittedEmail, submittedPassword, rememberMe);

      if (result.success && result.redirectTo) {
        toast({
          title: "Welcome back!",
          description: "You have successfully logged in",
        });
        setRedirectTo(result.redirectTo);
      } else {
        toast({
          title: "Login failed",
          description: result.error || "Invalid credentials. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup(email, password, "");

      if (result.success) {
        toast({
          title: "Account created!",
          description: "You can now log in with your credentials",
        });
        setMode("login");
        setPassword("");
      } else {
        toast({
          title: "Signup failed",
          description:
            result.error || "Could not create account. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const passwordToggle = (
    <button
      type="button"
      className="au-toggle"
      onClick={() => setShowPassword(!showPassword)}
      aria-label={showPassword ? "Hide password" : "Show password"}
      aria-pressed={showPassword}
    >
      {showPassword ? (
        <EyeOff aria-hidden="true" />
      ) : (
        <Eye aria-hidden="true" />
      )}
    </button>
  );

  return (
    <AuthShell
      eyebrow="Content Administration"
      headline={["Jambushrushti", "Portal"]}
      intro="Publish Guruvani, books, events, the eMagazine and everything else visitors see on the Jambushrushti website."
      icon={<Shield />}
      title={mode === "signup" ? "Create Account" : "Admin Sign In"}
      subtitle="Admin & Content Management System"
    >
      {mode === "login" ? (
        <form onSubmit={handleLogin} className="au-form">
          <AuthField id="login-email" label="Email Address" icon={<Mail />}>
            <input
              ref={emailInputRef}
              id="login-email"
              type="email"
              placeholder="admin@jambushrusti.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
            />
          </AuthField>

          <AuthField
            id="login-password"
            label="Password"
            icon={<Lock />}
            trailing={passwordToggle}
          >
            <input
              ref={passwordInputRef}
              id="login-password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </AuthField>

          <button
            type="button"
            onClick={() => void handleLogin()}
            className="au-submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="au-spin" aria-hidden="true" />
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSignup} className="au-form">
          <AuthField id="signup-email" label="Email Address" icon={<Mail />}>
            <input
              id="signup-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
            />
          </AuthField>

          <AuthField
            id="signup-password"
            label="Password"
            icon={<Lock />}
            trailing={passwordToggle}
          >
            <input
              id="signup-password"
              type={showPassword ? "text" : "password"}
              placeholder="Min. 6 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
            />
          </AuthField>

          <button type="submit" className="au-submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="au-spin" aria-hidden="true" />
                Creating account...
              </>
            ) : (
              <>
                Create Account
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
